import { useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import APIService from '../../../Services/APIService';
import { Apis } from '../../../Services/app.constants';
import { organisationAccountsRefreshAtom } from '../state/accountAtoms';

function parseAccountsError(raw, fallback = 'Unable to load accounts') {
  const value = raw || fallback;
  return typeof value === 'string'
    ? value.replace(/^\(\s*['"]|['"]\s*,?\s*\)$/g, '')
    : String(value);
}

/**
 * Loads accounts for an organisation, or every organisation for platform super admins.
 *
 * @param {string} [organisationId]
 * @param {{ allowAll?: boolean }} [options]
 */
export default function useOrganisationAccounts(organisationId, { allowAll = false } = {}) {
  const refreshToken = useRecoilValue(organisationAccountsRefreshAtom);
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasLoadedRef = useRef(false);

  const fetchAccounts = useCallback(async () => {
    if (!organisationId && !allowAll) {
      hasLoadedRef.current = false;
      setAccounts([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    if (!hasLoadedRef.current) {
      setIsLoading(true);
    }
    setError(null);

    try {
      const query = organisationId
        ? `?organisation_id=${encodeURIComponent(organisationId)}`
        : '';
      const res = await APIService.get(`${Apis.getAccountsList}${query}`);
      if (res?.status === 200) {
        const list = res.data?.list || res.data || [];
        setAccounts(Array.isArray(list) ? list : []);
      } else {
        setAccounts([]);
        const raw =
          res?.data?.data?.system_error ||
          res?.data?.data?.general_error ||
          res?.data?.system_error ||
          res?.data?.general_error ||
          res?.data?.message ||
          'Unable to load accounts';
        setError(parseAccountsError(raw));
      }
    } catch (err) {
      setAccounts([]);
      const raw =
        err?.response?.data?.data?.system_error ||
        err?.response?.data?.data?.general_error ||
        err?.response?.data?.system_error ||
        err?.response?.data?.general_error ||
        err?.response?.data?.message ||
        err?.message ||
        'Unable to load accounts';
      setError(parseAccountsError(raw));
    } finally {
      hasLoadedRef.current = true;
      setIsLoading(false);
    }
  }, [allowAll, organisationId]);

  useEffect(() => {
    hasLoadedRef.current = false;
  }, [organisationId]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts, refreshToken]);

  const patchAccountInList = useCallback((accountId, patch) => {
    setAccounts((current) => current.map((account) => (
      account?.account_id === accountId
        ? { ...account, ...patch }
        : account
    )));
  }, []);

  return { accounts, isLoading, error, refetch: fetchAccounts, patchAccountInList };
}
